var HK_LOGIN_POPUP_COOKIE = 'hk_header_account_login_popup';
var HK_LOGIN_POPUP_MAX_AGE_SEC = 86400;

class AccountAuthPopup extends HTMLElement {
  constructor() {
    super();
    this._onAuthOpen = this._onAuthOpen.bind(this);
    this._onResize = this._onResize.bind(this);
    this._dialogEl = null;
    this._dialogAttached = false;
    this._resizeBound = false;
    this._deferredPromptTimeout = null;
    this._deferredLoadListener = null;
    this._observePollTimeout = null;
    this._observeAttempts = 0;
    this._pendingOpenTimeout = null;
    this._pendingOpenAttempts = 0;
  }

  connectedCallback() {
    window.addEventListener('auth:open_popup', this._onAuthOpen);
    this._observeDialog();
    this._scheduleGuestLoginReminderOncePerDay();
    this._scheduleAutoOpenOnMobile();
  }

  disconnectedCallback() {
    window.removeEventListener('auth:open_popup', this._onAuthOpen);
    if (this._dialogObserver) {
      this._dialogObserver.disconnect();
      this._dialogObserver = null;
    }
    if (this._resizeBound) {
      window.removeEventListener('resize', this._onResize);
      this._resizeBound = false;
    }
    if (this._deferredPromptTimeout) {
      clearTimeout(this._deferredPromptTimeout);
      this._deferredPromptTimeout = null;
    }
    if (this._deferredLoadListener) {
      window.removeEventListener('load', this._deferredLoadListener);
      this._deferredLoadListener = null;
    }
    if (this._observePollTimeout) {
      clearTimeout(this._observePollTimeout);
      this._observePollTimeout = null;
    }
    if (this._pendingOpenTimeout) {
      clearTimeout(this._pendingOpenTimeout);
      this._pendingOpenTimeout = null;
    }
    this._dialogEl = null;
    this._dialogAttached = false;
  }

  _getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(';').shift() || '');
    }
    return null;
  }

  _setCookie(name, value, maxAgeSeconds) {
    var secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; max-age=' +
      String(maxAgeSeconds) +
      '; path=/; SameSite=Lax' +
      secure;
  }

  /**
   * Guests only (via Liquid data attribute). At most one automatic prompt per day;
   * runs only after window load so the initial paint stays free of the modal.
   */
  _scheduleGuestLoginReminderOncePerDay() {
    if (this.dataset.loginReminderOncePerDay !== 'true') return;
    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) return;
    if (window.innerWidth <= 750) return;
    if (this._getCookie(HK_LOGIN_POPUP_COOKIE)) return;

    var self = this;
    var delayAfterLoadMs = 700;
    var run = function () {
      self._deferredPromptTimeout = null;
      if (self._getCookie(HK_LOGIN_POPUP_COOKIE)) return;
      self._onAuthOpen();
    };

    if (document.readyState === 'complete') {
      this._deferredPromptTimeout = window.setTimeout(run, delayAfterLoadMs);
    } else {
      this._deferredLoadListener = function () {
        window.removeEventListener('load', self._deferredLoadListener);
        self._deferredLoadListener = null;
        self._deferredPromptTimeout = window.setTimeout(run, delayAfterLoadMs);
      };
      window.addEventListener('load', this._deferredLoadListener);
    }
  }

  /**
   * Auto-open the popup on mobile when explicitly enabled via data attribute.
   * Bypasses the once-per-day cookie throttle so it fires every page visit.
   */
  _scheduleAutoOpenOnMobile() {
    if (this.dataset.autoOpenOnMobile !== 'true') return;
    if (window.innerWidth > 750) return;
    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) return;
    if (this._getCookie(HK_LOGIN_POPUP_COOKIE)) return;

    var self = this;
    var run = function () {
      if (self._getCookie(HK_LOGIN_POPUP_COOKIE)) return;
      self._onAuthOpen();
    };

    if (document.readyState === 'complete') {
      window.setTimeout(run, 500);
    } else {
      window.addEventListener('load', function onLoad() {
        window.removeEventListener('load', onLoad);
        window.setTimeout(run, 500);
      });
    }
  }

  _onResize() {
    var dialog = this._dialogEl;
    if (!dialog) return;
    if (dialog.hasAttribute('open') || dialog.open) {
      this._positionDialog(dialog);
    }
  }

  /**
   * Attach a MutationObserver to the <shopify-account> dialog. Because
   * attachShadow() doesn't trigger childList mutations and shadow-internal
   * mutations don't escape the shadow boundary, we can't reliably detect
   * shadow root creation with a MutationObserver. Poll instead, with a
   * generous max-attempt cap so the listener survives a slow upgrade of
   * the underlying <shopify-account> custom element.
   */
  _observeDialog() {
    var self = this;
    var account = this.accountElement;
    if (!account) return;

    var tryAttach = function () {
      if (self._dialogAttached) return true;
      if (!account.shadowRoot) return false;
      var dialog = account.shadowRoot.querySelector('dialog');
      if (!dialog) return false;

      self._dialogAttached = true;
      self._dialogEl = dialog;

      self._dialogObserver = new MutationObserver(function () {
        if (dialog.hasAttribute('open') || dialog.open) {
          self._positionDialog(dialog);
          self._hideShortcutsOnRoute(dialog);
          self._setCookie(HK_LOGIN_POPUP_COOKIE, '1', HK_LOGIN_POPUP_MAX_AGE_SEC);
        }
      });
      self._dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });

      if (!self._resizeBound) {
        window.addEventListener('resize', self._onResize);
        self._resizeBound = true;
      }
      return true;
    };

    if (tryAttach()) return;

    var poll = function () {
      self._observePollTimeout = null;
      if (self._dialogAttached) return;
      if (tryAttach()) return;
      self._observeAttempts++;
      if (self._observeAttempts > 120) return;
      var delay = self._observeAttempts < 10 ? 50 : self._observeAttempts < 40 ? 150 : 400;
      self._observePollTimeout = window.setTimeout(poll, delay);
    };

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('shopify-account').then(poll, poll);
    }
    self._observePollTimeout = window.setTimeout(poll, 0);
  }

  get accountElement() {
    return this.querySelector('shopify-account');
  }

  _positionDialog(dialog) {
    if (window.innerWidth <= 750) return;

    var header = document.querySelector('header.custom__header__wrapper');
    if (!header) return;
    var topOffset = header.getBoundingClientRect().bottom;

    // the spacing visually matches the rounded top-right login component
    var dialogStyles = window.getComputedStyle(dialog);
    var rightInset = (parseFloat(dialogStyles.borderTopRightRadius) || 0) / 2 + 6;

    if (window.HKBooking && window.HKBooking.isBookingPath()) {
      var experiencesBar = document.querySelector('.tw-bg-\\[\\#393B41\\]');
      if (experiencesBar) {
        topOffset = experiencesBar.getBoundingClientRect().bottom + rightInset;
      }
    }

    dialog.style.setProperty('top', topOffset + 'px', 'important');
    dialog.style.setProperty('right', rightInset + 'px', 'important');
    dialog.style.setProperty('left', 'auto', 'important');
    dialog.style.setProperty('margin', '0', 'important');
  }

  _hideShortcutsOnRoute(dialog) {
    var shortcuts = dialog.querySelector('nav.account__shortcuts');
    var isMobile = window.innerWidth <= 750;
    
    if (!shortcuts) return;
    var hide = Boolean(window.HKBooking && window.HKBooking.isBookingPath()) && isMobile;

    if(isMobile) {
      shortcuts.style.setProperty('display', isMobile ? 'none' : '', isMobile ? 'important' : '');
    }

    if (hide) {
      dialog.style.setProperty('--dialog-min-height', '0', 'important');
    } else {
      dialog.style.removeProperty('--dialog-min-height');
    }
  }

  _buildSignInUrl() {
    // Shopify can only serve the booking page's own URL, not the app's deep
    // paths, so `resolveReturnUrl` stashes the current step and returns the
    // servable URL. The app replays the step and restores /booking/races/... .
    var returnToPath = window.HKBooking ? window.HKBooking.resolveReturnUrl() : window.location.pathname + window.location.search;

    return '/customer_authentication/login?return_to=' + encodeURIComponent(returnToPath);
  }

  _onAuthOpen(event) {
    if (this.offsetParent === null) return;

    var account = this.accountElement;
    if (!account) return;

    var dialog = account.shadowRoot ? account.shadowRoot.querySelector('dialog') : null;

    if (!dialog) {
      // <shopify-account> hasn't finished upgrading / attaching its shadow
      // root yet. Retry briefly so the open intent is honoured even when
      // our custom element upgrades before Shopify's.
      this._scheduleRetryOpen(event);
      return;
    }

    this._pendingOpenAttempts = 0;
    if (this._pendingOpenTimeout) {
      clearTimeout(this._pendingOpenTimeout);
      this._pendingOpenTimeout = null;
    }

    this._positionDialog(dialog);
    this._hideShortcutsOnRoute(dialog);

    if (typeof window.updateHeaderAccountSignInUrl === 'function') {
      window.updateHeaderAccountSignInUrl();
    } else {
      account.setAttribute('sign-in-url', this._buildSignInUrl());
    }

    if (typeof window.__setLoginRedirectCookie === 'function') {
      window.__setLoginRedirectCookie();
    }

    if (typeof dialog.showModal === 'function' && !dialog.open) {
      try {
        dialog.showModal();
        return;
      } catch (e) {}
    }

    if (account.firstElementChild) {
      account.firstElementChild.click();
    } else {
      account.click();
    }
  }

  _scheduleRetryOpen(event) {
    if (this._pendingOpenTimeout) return;
    if (this._pendingOpenAttempts > 60) return;

    var self = this;
    var delay = this._pendingOpenAttempts < 10 ? 50 : this._pendingOpenAttempts < 30 ? 150 : 400;
    this._pendingOpenAttempts++;
    this._pendingOpenTimeout = window.setTimeout(function () {
      self._pendingOpenTimeout = null;
      self._onAuthOpen(event);
    }, delay);
  }
}

if (!customElements.get('account-auth-popup')) {
  customElements.define('account-auth-popup', AccountAuthPopup);
}
